import { type UserRole } from '@adweb/shared';
import axios, { type AxiosInstance } from 'axios';

const baseURL = (import.meta.env.VITE_API_URL as string | undefined) ?? '/api/v1';

export const apiClient: AxiosInstance = axios.create({
  baseURL,
  withCredentials: true,
});

export interface SessionUser {
  id: string;
  email: string;
  fullName: string;
  organizationId: string;
  role: UserRole;
  twoFactorEnabled: boolean;
}

export interface Session {
  accessToken: string;
  user: SessionUser;
}

// ── État de session en mémoire ──────────────────────────────────────────────
// Jamais en localStorage (règle projet : pas de données sensibles persistées).
let currentSession: Session | null = null;
let lastRefreshAt = 0;

export function getAccessToken(): string | null {
  return currentSession?.accessToken ?? null;
}

// ── Abonnement (l'auth-context reflète les changements de session) ──────────
type SessionListener = (session: Session | null) => void;
let sessionListener: SessionListener | null = null;
export function onSessionChange(cb: SessionListener | null): void {
  sessionListener = cb;
}

// ── Coordination multi-onglets via BroadcastChannel ─────────────────────────
// Le canal reste en mémoire (rien sur disque) : on diffuse la session courante
// aux autres onglets pour qu'ils ne déclenchent pas leur propre refresh.
const channel: BroadcastChannel | null =
  typeof BroadcastChannel !== 'undefined' ? new BroadcastChannel('adweb-auth') : null;

if (channel) {
  channel.onmessage = (event: MessageEvent) => {
    const msg = event.data as { type: 'session' | 'logout'; session?: Session; at?: number };
    if (msg.type === 'session' && msg.session) {
      currentSession = msg.session;
      lastRefreshAt = msg.at ?? Date.now();
      sessionListener?.(msg.session);
    } else if (msg.type === 'logout') {
      currentSession = null;
      sessionListener?.(null);
    }
  };
}

/** Enregistre une session (login, 2FA, refresh) et la diffuse aux autres onglets. */
export function applySession(session: Session): void {
  currentSession = session;
  lastRefreshAt = Date.now();
  channel?.postMessage({ type: 'session', session, at: lastRefreshAt });
  sessionListener?.(session);
}

/** Termine la session localement et dans les autres onglets. */
export function clearSession(): void {
  currentSession = null;
  channel?.postMessage({ type: 'logout' });
  sessionListener?.(null);
}

// ── Requête : injecte le bearer courant ─────────────────────────────────────
apiClient.interceptors.request.use((config) => {
  const token = currentSession?.accessToken;
  if (token) {
    config.headers.set('Authorization', `Bearer ${token}`);
  }
  return config;
});

// ── Refresh : single-flight (intra-onglet) + verrou Web Locks (inter-onglets)
interface LockManagerLike {
  request<T>(name: string, callback: () => Promise<T>): Promise<T>;
}

let inFlight: Promise<Session | null> | null = null;

async function performRefresh(requestedAt: number): Promise<Session | null> {
  const run = async (): Promise<Session | null> => {
    // Un autre onglet a pu rafraîchir pendant qu'on attendait le verrou : on réutilise.
    if (currentSession && lastRefreshAt > requestedAt) {
      return currentSession;
    }
    try {
      const res = await axios.post<{ accessToken?: string; user?: SessionUser }>(
        `${baseURL}/auth/refresh`,
        {},
        { withCredentials: true },
      );
      if (res.data.accessToken && res.data.user) {
        const session: Session = { accessToken: res.data.accessToken, user: res.data.user };
        applySession(session);
        return session;
      }
      return null;
    } catch {
      return null;
    }
  };

  // Sérialise le refresh entre tous les onglets de la même origine si dispo.
  const locks =
    typeof navigator !== 'undefined'
      ? (navigator as unknown as { locks?: LockManagerLike }).locks
      : undefined;
  if (locks?.request) {
    return locks.request('adweb-auth-refresh', run);
  }
  return run();
}

/** Rafraîchit la session — un seul appel réseau à la fois par onglet. */
export function refreshAuth(): Promise<Session | null> {
  if (inFlight) return inFlight;
  const requestedAt = Date.now();
  inFlight = performRefresh(requestedAt).finally(() => {
    inFlight = null;
  });
  return inFlight;
}

// ── Réponse : sur 401, tente un refresh puis rejoue la requête une fois ──────
apiClient.interceptors.response.use(
  (res) => res,
  async (error) => {
    if (
      error?.response?.status === 401 &&
      error.config &&
      !error.config._retried &&
      !error.config.url?.includes('/auth/login') &&
      !error.config.url?.includes('/auth/refresh')
    ) {
      const session = await refreshAuth();
      if (session) {
        error.config._retried = true;
        return apiClient(error.config);
      }
      // Refresh définitivement échoué → fin de session (l'auth-context redirige).
      clearSession();
    }
    return Promise.reject(error);
  },
);
